/**
 * Wire types for the /api/client-telemetry endpoint.
 *
 * These mirror the backend's `pkg/api/telemetry.CeremonyEventRequest`
 * 1:1 (snake_case keys, optional fields elided when empty). Keep
 * the shapes in sync — drift here will surface as 400 from the
 * server with `EventsRejected` ticking up on the operator
 * dashboard.
 *
 * Higher-level callers use the camelCase `ReportCeremonyEventInput`
 * exposed from `./reporter`; this file is the wire-side narrow
 * waist that the reporter serialises down to.
 */

import type { CapabilitySnapshot } from "../device-capabilities";

/**
 * `surface_entered` / `surface_abandoned` are funnel-measurement
 * phases (P0d). `surface_entered` fires once when an onboarding
 * surface mounts (and diagnostics is wired); `surface_abandoned`
 * fires on unmount or `beforeunload` IFF the surface did not
 * reach success.
 *
 * Both events carry a coarse surface label in `code` (e.g.
 * `"login_otp"`, `"enroll_passkey"`) so dashboards can split
 * abandonment by the deepest reached step. Neither event carries
 * a transport_attempted, error_name, or error_message — they
 * record the funnel boundary, not a ceremony failure.
 */
export type TelemetryPhase =
  | "preflight"
  | "begin"
  | "create"
  | "get"
  | "finish"
  | "surface_entered"
  | "surface_abandoned";

export type TelemetryOutcome = "ok" | "error" | "abort";

// One transport vocabulary, shared with the UI's PasskeyTransport
// + the classifier's AttemptedTransport (P2-2 collapse). "No
// transport picked" is undefined/omitted on the wire, not a
// pseudo-member; conditional mediation rides the separate
// conditional_mediation flag.
export type TelemetryTransport = "this_device" | "other_device" | "security_key";

export interface CeremonyTelemetryEventWire {
  phase: TelemetryPhase;
  outcome: TelemetryOutcome;
  /** Closed-set classifier code; matches backend regex `^[a-z][a-z0-9_-]{0,63}$`. */
  code: string;
  error_name?: string;
  error_message?: string;
  duration_ms?: number;
  transport_attempted?: TelemetryTransport;
  capability: CapabilitySnapshotWire;
  ua: UaHintWire;
  /** Server's ceremony session_id when known (post-/begin events only). */
  session_id?: string;
}

/**
 * Wire-side mirror of `CapabilitySnapshot`. The two non-pointer
 * fields (`secure_context`, `webauthn`) are non-nullable in the
 * wire format because their values are proven by the page already
 * running this code — there is no "couldn't determine" state for
 * either.
 */
export interface CapabilitySnapshotWire {
  platform_authenticator: boolean | null;
  conditional_mediation: boolean | null;
  secure_context: boolean;
  webauthn: boolean;
}

/** UA hint as parsed by `parseUaHint`. */
export interface UaHintWire {
  browser: string;
  os: string;
  version: string;
}

/**
 * Map a `CapabilitySnapshot` (camelCase, used everywhere on the
 * client) to its wire-side representation. Pure function — used
 * by the reporter and exposed for tests that want to assert
 * round-trip identity.
 */
export function capabilityToWire(c: CapabilitySnapshot): CapabilitySnapshotWire {
  return {
    platform_authenticator: c.platformAuthenticator,
    conditional_mediation: c.conditionalMediation,
    secure_context: c.secureContext,
    webauthn: c.webauthn,
  };
}
